/**
 * ExecutiveDashboard Component
 * Composed executive dashboard view with all executive report components
 *
 * Story 2.5: Executive Dashboard Composition
 */

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { clsx } from 'clsx';
import { Target, TrendingUp, AlertTriangle, CheckCircle } from 'lucide-react';
import {
  useExecutiveSummary,
  useStatusDistribution,
  useProgressByDepartment,
} from '../../../hooks/useReportData';
import { buildOKRListURL } from '../../../utils/okrListURL';
import { ExecutiveSummaryCards } from './SummaryCard';
import { TeamLeaderboard } from './TeamLeaderboard';
import { StatusDistributionChart } from '../charts/StatusDistributionChart';
import { DepartmentProgressChart } from '../charts/DepartmentProgressChart';
import { ReportErrorState, ExecutiveDashboardSkeleton } from '../index';
import type { DateRange, ReportFilters } from '../index';

export interface ExecutiveDashboardProps {
  /** Date range for the report */
  dateRange: DateRange;
  /** Filters applied to the report */
  filters?: ReportFilters;
  /** Additional class names */
  className?: string;
}

/**
 * Convert DateRange to API format
 */
function toApiDateRange(dateRange: DateRange) {
  return {
    startDate: dateRange.startDate.toISOString().split('T')[0],
    endDate: dateRange.endDate.toISOString().split('T')[0],
  };
}

/**
 * Convert ReportFilters to API format
 */
function toApiFilters(filters?: ReportFilters) {
  if (!filters) return undefined;
  return {
    departmentIds: filters.departmentIds.length > 0 ? filters.departmentIds : undefined,
    teamIds: filters.teamIds.length > 0 ? filters.teamIds : undefined,
    ownerIds: filters.ownerIds.length > 0 ? filters.ownerIds : undefined,
  };
}

/**
 * Executive Dashboard Component
 */
export const ExecutiveDashboard: React.FC<ExecutiveDashboardProps> = ({
  dateRange,
  filters,
  className,
}) => {
  const navigate = useNavigate();

  // Convert to API formats
  const apiDateRange = toApiDateRange(dateRange);
  const apiFilters = toApiFilters(filters);

  // Fetch all data
  const {
    data: summaryData,
    isLoading: summaryLoading,
    error: summaryError,
    refetch: refetchSummary,
  } = useExecutiveSummary(apiDateRange, apiFilters);

  const {
    data: statusData,
    isLoading: statusLoading,
    error: statusError,
    refetch: refetchStatus,
  } = useStatusDistribution(apiDateRange, apiFilters);

  const {
    data: departmentData,
    isLoading: departmentLoading,
    error: departmentError,
    refetch: refetchDepartment,
  } = useProgressByDepartment(apiDateRange, apiFilters);

  // Check for any errors
  const hasError = summaryError || statusError || departmentError;
  const isLoading = summaryLoading && statusLoading && departmentLoading;

  // Drill-down handlers.
  //
  // Twice now a tile has navigated with a param the OKR list does not read, and both times it
  // failed SILENTLY — an unread param yields an unfiltered list, which looks like a working
  // page full of data. First `?status=` (the list reads plural `statuses`; fixed S1-g,
  // 2026-08-12), then `?department=` (the list reads `departmentIds`; fixed 2026-08-13).
  //
  // Every link now goes through `buildOKRListURL`, which serialises via the same
  // `filtersToURLParams` that `parseFiltersFromURL` is the inverse of — so a param name can no
  // longer drift apart from its reader without the round-trip test failing.
  const handleDrillDown = {
    totalOKRs: () => navigate(buildOKRListURL({})),
    completionRate: () => navigate(buildOKRListURL({ statuses: ['completed'] })),
    averageProgress: () => navigate(buildOKRListURL({})),
    atRisk: () => navigate(buildOKRListURL({ statuses: ['at_risk', 'off_track'] })),
  };

  const handleStatusClick = (status: string) => {
    navigate(buildOKRListURL({ statuses: [status] }));
  };

  // The chart bar and the leaderboard row both drill down by department ID: the OKR list's
  // `departmentIds` takes Department uuids and filters on the owner's memberships, the same
  // as these reports. (It once took `?department=`, a param the list never read, and later
  // names; both landed on a list that did not match the bar.) The "No department" row has no
  // id, and the list has no "no department" filter, so it opens the list unfiltered.
  const handleDepartmentClick = (departmentId: string | null) => {
    navigate(buildOKRListURL(departmentId ? { departmentIds: [departmentId] } : {}));
  };

  const handleRetry = () => {
    refetchSummary();
    refetchStatus();
    refetchDepartment();
  };

  // Show full loading state if all data is loading
  if (isLoading) {
    return <ExecutiveDashboardSkeleton />;
  }

  // Show error state if there's a critical error
  if (hasError && !summaryData && !statusData && !departmentData) {
    const errorToShow = summaryError || statusError || departmentError;
    return (
      <ReportErrorState
        error={errorToShow instanceof Error ? errorToShow : undefined}
        onRetry={handleRetry}
        className={className}
      />
    );
  }

  return (
    <div className={clsx('space-y-6', className)}>
      {/* Summary Cards */}
      <ExecutiveSummaryCards
        totalOKRs={summaryData?.totalOKRs ?? 0}
        completionRate={summaryData?.completionRate ?? 0}
        averageProgress={summaryData?.averageProgress ?? 0}
        atRiskCount={summaryData?.atRiskCount ?? 0}
        atRiskPercentage={summaryData?.atRiskPercentage ?? 0}
        changes={{
          totalOKRs: summaryData?.totalOKRsChange,
          completionRate: summaryData?.completionRateChange,
          averageProgress: summaryData?.averageProgressChange,
        }}
        onDrillDown={handleDrillDown}
        isLoading={summaryLoading}
      />

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Status Distribution Chart */}
        <StatusDistributionChart
          data={statusData ?? []}
          title="OKR Status Distribution"
          onStatusClick={handleStatusClick}
          isLoading={statusLoading}
        />

        {/* Department Progress Chart */}
        <DepartmentProgressChart
          data={departmentData ?? []}
          title="Progress by Department"
          onDepartmentClick={handleDepartmentClick}
          isLoading={departmentLoading}
          height={280}
        />
      </div>

      {/* Bottom Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Team Leaderboard */}
        <TeamLeaderboard
          data={
            departmentData?.map((d) => ({
              ...d,
            })) ?? []
          }
          title="Top Performing Teams"
          limit={5}
          minOKRCount={1}
          onTeamClick={handleDepartmentClick}
          isLoading={departmentLoading}
        />

        {/* Key Insights Card */}
        <KeyInsightsCard
          summaryData={summaryData}
          departmentData={departmentData}
          isLoading={summaryLoading || departmentLoading}
        />
      </div>
    </div>
  );
};

/**
 * Key Insights Card - auto-generated insights
 */
interface KeyInsightsCardProps {
  summaryData?: {
    totalOKRs: number;
    completionRate: number;
    averageProgress: number;
    atRiskCount: number;
    atRiskPercentage: number;
  };
  departmentData?: Array<{
    department: string;
    okrCount: number;
    averageProgress: number;
    completionRate: number;
  }>;
  isLoading: boolean;
}

const KeyInsightsCard: React.FC<KeyInsightsCardProps> = ({
  summaryData,
  departmentData,
  isLoading,
}) => {
  if (isLoading) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-stone-200 p-6">
        <div className="h-5 bg-stone-200 rounded w-32 mb-4 animate-pulse" />
        <div className="space-y-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className="flex items-start gap-3 p-3 bg-stone-50 rounded-lg">
              <div className="w-8 h-8 rounded-full bg-stone-200 animate-pulse" />
              <div className="flex-1">
                <div className="h-4 bg-stone-200 rounded w-full mb-2 animate-pulse" />
                <div className="h-3 bg-stone-200 rounded w-3/4 animate-pulse" />
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  // Generate insights based on data
  const insights: Array<{ icon: React.ReactNode; title: string; description: string; type: 'success' | 'warning' | 'info' }> = [];

  if (summaryData) {
    // Progress insight
    if (summaryData.averageProgress >= 70) {
      insights.push({
        icon: <TrendingUp className="w-5 h-5 text-green-600" />,
        title: 'Strong overall progress',
        description: `Your organization is averaging ${summaryData.averageProgress}% progress across all OKRs.`,
        type: 'success',
      });
    } else if (summaryData.averageProgress < 40) {
      insights.push({
        icon: <AlertTriangle className="w-5 h-5 text-red-600" />,
        title: 'Progress needs attention',
        description: `Average progress is ${summaryData.averageProgress}%. Consider reviewing blockers with teams.`,
        type: 'warning',
      });
    }

    // At-risk insight
    if (summaryData.atRiskPercentage > 20) {
      insights.push({
        icon: <AlertTriangle className="w-5 h-5 text-amber-600" />,
        title: 'High at-risk percentage',
        description: `${summaryData.atRiskPercentage}% of OKRs are at risk. Prioritize check-ins with affected teams.`,
        type: 'warning',
      });
    }

    // Completion insight
    if (summaryData.completionRate >= 50) {
      insights.push({
        icon: <CheckCircle className="w-5 h-5 text-green-600" />,
        title: 'Good completion rate',
        description: `${summaryData.completionRate}% of OKRs have been completed this period.`,
        type: 'success',
      });
    }
  }

  if (departmentData && departmentData.length > 0) {
    // Top performer insight
    const topDept = departmentData.reduce((a, b) =>
      a.averageProgress > b.averageProgress ? a : b
    );
    if (topDept.averageProgress > 0) {
      insights.push({
        icon: <Target className="w-5 h-5 text-primary-600" />,
        title: `${topDept.department} leads the pack`,
        description: `With ${topDept.averageProgress}% average progress across ${topDept.okrCount} OKRs.`,
        type: 'info',
      });
    }
  }

  // Add default insight if none generated
  if (insights.length === 0) {
    insights.push({
      icon: <Target className="w-5 h-5 text-stone-400" />,
      title: 'Getting started',
      description: 'Create more OKRs to see personalized insights about your organization\'s performance.',
      type: 'info',
    });
  }

  return (
    <div className="bg-white rounded-lg shadow-sm border border-stone-200 p-6">
      <h3 className="text-lg font-semibold text-stone-900 mb-4">Key Insights</h3>
      <div className="space-y-3">
        {insights.map((insight, index) => (
          <div
            key={index}
            className={clsx(
              'flex items-start gap-3 p-3 rounded-lg',
              insight.type === 'success' && 'bg-green-50',
              insight.type === 'warning' && 'bg-amber-50',
              insight.type === 'info' && 'bg-stone-50'
            )}
          >
            <div className="flex-shrink-0 mt-0.5">{insight.icon}</div>
            <div>
              <div className="font-medium text-stone-900">{insight.title}</div>
              <div className="text-sm text-stone-600">{insight.description}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default ExecutiveDashboard;
